import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";
import { numFont } from "../../utils/fonts";
import { formatPrice, formatPriceWithCurrency } from "../../utils/vat";

// Nets prices quoted to workshops, all VAT 0 %. Rent is per terminal model.
const NETS_PRICES = {
  debitPercent: 0.39,
  creditPercent: 0.79,
  corporatePercent: 1.79,
  perTransaction: 0.03,
  contractMonths: 36,
  openingPerTerminal: 30,
  // Nets bills delivery in tiers by terminal count; a workshop orders 1-2.
  delivery: 14.9,
};

// Brochures are served from frontend/public/docs/maksupaatteet/.
const TERMINAL_MODELS = [
  {
    value: "p300",
    name: "Nexi SmartPOS P300",
    shortName: "P300",
    rentPerMonth: 29,
    brochure: "/docs/maksupaatteet/nets-p300.pdf",
  },
  {
    value: "n950",
    name: "Nets SmartPOS N950",
    shortName: "N950",
    rentPerMonth: 34,
    brochure: "/docs/maksupaatteet/nets-n950.pdf",
  },
];

// Maventa receives e-invoices for the workshops that send through it.
const MAVENTA_OPERATOR = "Maventa (003721291126)";

const percent = (value) => `${formatPrice(value)} %`;

function PriceRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.25 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ ...numFont, whiteSpace: "nowrap" }}>
        {value}
      </Typography>
    </Box>
  );
}

/** Nets commission and rental prices shown before a workshop orders a terminal. Collapsed by default. */
export function TerminalPriceList() {
  const { t } = useTranslation();
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, "&:before": { display: "none" } }}
      data-testid="terminal-price-list"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="terminal-price-list-toggle">
        <Typography variant="subtitle2">{t("terminal.order.prices.title")}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {t("terminal.order.prices.commissions")}
        </Typography>
        <PriceRow label={t("terminal.order.prices.debit")} value={percent(NETS_PRICES.debitPercent)} />
        <PriceRow label={t("terminal.order.prices.credit")} value={percent(NETS_PRICES.creditPercent)} />
        <PriceRow label={t("terminal.order.prices.corporate")} value={percent(NETS_PRICES.corporatePercent)} />
        <PriceRow
          label={t("terminal.order.prices.perTransaction")}
          value={formatPriceWithCurrency(NETS_PRICES.perTransaction)}
        />
        <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
          {t("terminal.order.prices.terminal")}
        </Typography>
        <PriceRow
          label={t("terminal.order.prices.contractPeriod")}
          value={t("terminal.order.prices.months", { count: NETS_PRICES.contractMonths })}
        />
        {TERMINAL_MODELS.map((model) => (
          <PriceRow
            key={model.value}
            label={t("terminal.order.prices.rent", { model: model.shortName })}
            value={t("terminal.order.prices.perMonth", { price: formatPriceWithCurrency(model.rentPerMonth) })}
          />
        ))}
        <PriceRow
          label={t("terminal.order.prices.opening")}
          value={formatPriceWithCurrency(NETS_PRICES.openingPerTerminal)}
        />
        <PriceRow label={t("terminal.order.prices.delivery")} value={formatPriceWithCurrency(NETS_PRICES.delivery)} />
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {t("terminal.order.prices.vatNote")}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

const EMPTY_FORM = {
  is_nets_customer: "",
  terminal_model: "",
  invoice_method: "",
  invoice_email: "",
  einvoice_address: "",
  einvoice_operator: "",
  site_street: "",
  site_postal_code: "",
  site_city: "",
};

/** Prefill from what the organization settings already know. */
function formFromOrganization(organization) {
  if (!organization) return EMPTY_FORM;
  const usesMaventa = Boolean(organization.maventa_enabled && organization.ovt_address);
  return {
    ...EMPTY_FORM,
    invoice_email: organization.invoice_email || organization.contact_email || "",
    einvoice_address: usesMaventa ? organization.ovt_address : "",
    einvoice_operator: usesMaventa ? MAVENTA_OPERATOR : "",
    site_street: organization.address_street || "",
    site_postal_code: organization.address_postal_code || "",
    site_city: organization.address_city || "",
  };
}

/**
 * Payment terminal order form. Mails the answers to Verstaani support through
 * POST /api/organizations/{id}/terminal/request-contact/, which stamps the organization
 * so the order button disappears for good. Used in Settings and the onboarding checklist.
 */
function TerminalOrderDialog({ open, onClose, onSent, organization }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const organizationId = currentOrganization?.organization_id;

  const [form, setForm] = useState(EMPTY_FORM);
  const [loadingOrg, setLoadingOrg] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    setError(null);
    if (organization) {
      setForm(formFromOrganization(organization));
      return undefined;
    }
    // Opened without org data (onboarding checklist): fetch it for the prefill.
    let cancelled = false;
    setForm(EMPTY_FORM);
    if (organizationId) {
      setLoadingOrg(true);
      axios
        .get(`/api/organizations/${organizationId}/`)
        .then((response) => {
          if (!cancelled) setForm(formFromOrganization(response.data));
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setLoadingOrg(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [open, organization, organizationId]);

  const update = (field) => (event) => setForm((previous) => ({ ...previous, [field]: event.target.value }));

  const filled = (value) => value.trim().length > 0;
  const invoicingComplete =
    form.invoice_method === "email"
      ? filled(form.invoice_email)
      : form.invoice_method === "einvoice" && filled(form.einvoice_address) && filled(form.einvoice_operator);
  const canSend =
    form.is_nets_customer !== "" &&
    form.terminal_model !== "" &&
    invoicingComplete &&
    filled(form.site_street) &&
    filled(form.site_postal_code) &&
    filled(form.site_city);

  const handleSend = async () => {
    if (!organizationId || !canSend) return;
    setSending(true);
    setError(null);
    const emailInvoicing = form.invoice_method === "email";
    try {
      const response = await axios.post(`/api/organizations/${organizationId}/terminal/request-contact/`, {
        is_nets_customer: form.is_nets_customer === "yes",
        terminal_model: form.terminal_model,
        invoice_method: form.invoice_method,
        invoice_email: emailInvoicing ? form.invoice_email.trim() : "",
        einvoice_address: emailInvoicing ? "" : form.einvoice_address.trim(),
        einvoice_operator: emailInvoicing ? "" : form.einvoice_operator.trim(),
        site_street: form.site_street.trim(),
        site_postal_code: form.site_postal_code.trim(),
        site_city: form.site_city.trim(),
      });
      if (onSent) onSent(response.data);
    } catch (err) {
      if (err.response?.status === 409) {
        if (onSent) onSent(err.response.data);
      } else {
        setError(t("terminal.order.error"));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !sending && onClose()}
      maxWidth="sm"
      fullWidth
      data-testid="terminal-order-dialog"
    >
      <DialogTitle>{t("terminal.order.title")}</DialogTitle>
      <DialogContent>
        {loadingOrg ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            <FormControl>
              <FormLabel id="terminal-order-nets-label" sx={{ color: "text.primary", fontSize: "14px" }}>
                {t("terminal.order.netsCustomerQuestion")}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="terminal-order-nets-label"
                value={form.is_nets_customer}
                onChange={update("is_nets_customer")}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio size="small" data-testid="terminal-order-nets-yes" />}
                  label={t("terminal.order.yes")}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio size="small" data-testid="terminal-order-nets-no" />}
                  label={t("terminal.order.no")}
                />
              </RadioGroup>
            </FormControl>

            <FormControl>
              <FormLabel id="terminal-order-model-label" sx={{ color: "text.primary", fontSize: "14px", mb: 1 }}>
                {t("terminal.order.modelQuestion")}
              </FormLabel>
              <RadioGroup
                aria-labelledby="terminal-order-model-label"
                value={form.terminal_model}
                onChange={update("terminal_model")}
                sx={{ gap: 1 }}
              >
                {TERMINAL_MODELS.map((model) => (
                  <Box
                    key={model.value}
                    sx={{
                      border: "1px solid",
                      borderColor: form.terminal_model === model.value ? "primary.main" : "divider",
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <FormControlLabel
                      value={model.value}
                      control={<Radio size="small" data-testid={`terminal-order-model-${model.value}`} />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {model.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {t(`terminal.order.${model.value}Desc`)}
                          </Typography>
                          <Typography variant="caption" sx={{ ...numFont, fontWeight: 600 }}>
                            {t("terminal.order.prices.perMonth", { price: formatPriceWithCurrency(model.rentPerMonth) })}
                          </Typography>
                        </Box>
                      }
                    />
                    <Link
                      href={model.brochure}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                      data-testid={`terminal-order-brochure-${model.value}`}
                    >
                      <PictureAsPdfIcon fontSize="small" />
                      {t("terminal.order.brochure")}
                    </Link>
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>

            <FormControl>
              <FormLabel id="terminal-order-invoice-label" sx={{ color: "text.primary", fontSize: "14px" }}>
                {t("terminal.order.invoiceQuestion")}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="terminal-order-invoice-label"
                value={form.invoice_method}
                onChange={update("invoice_method")}
              >
                <FormControlLabel
                  value="email"
                  control={<Radio size="small" data-testid="terminal-order-invoice-email" />}
                  label={t("terminal.order.invoiceEmail")}
                />
                <FormControlLabel
                  value="einvoice"
                  control={<Radio size="small" data-testid="terminal-order-invoice-einvoice" />}
                  label={t("terminal.order.invoiceEinvoice")}
                />
              </RadioGroup>
              {form.invoice_method === "email" && (
                <TextField
                  label={t("terminal.order.invoiceEmailLabel")}
                  type="email"
                  value={form.invoice_email}
                  onChange={update("invoice_email")}
                  size="small"
                  fullWidth
                  required
                  sx={{ mt: 1 }}
                  inputProps={{ "data-testid": "terminal-order-invoice-email-input" }}
                />
              )}
              {form.invoice_method === "einvoice" && (
                <Box sx={{ display: "flex", gap: 1.5, mt: 1, flexWrap: "wrap" }}>
                  <TextField
                    label={t("terminal.order.einvoiceAddress")}
                    value={form.einvoice_address}
                    onChange={update("einvoice_address")}
                    size="small"
                    required
                    sx={{ flex: "1 1 200px" }}
                    inputProps={{ maxLength: 35, "data-testid": "terminal-order-einvoice-address" }}
                  />
                  <TextField
                    label={t("terminal.order.einvoiceOperator")}
                    value={form.einvoice_operator}
                    onChange={update("einvoice_operator")}
                    helperText={t("terminal.order.einvoiceOperatorHelp")}
                    size="small"
                    required
                    sx={{ flex: "1 1 200px" }}
                    inputProps={{ maxLength: 100, "data-testid": "terminal-order-einvoice-operator" }}
                  />
                </Box>
              )}
            </FormControl>

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("terminal.order.siteTitle")}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <TextField
                  label={t("terminal.order.street")}
                  value={form.site_street}
                  onChange={update("site_street")}
                  size="small"
                  fullWidth
                  required
                  inputProps={{ "data-testid": "terminal-order-site-street" }}
                />
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  <TextField
                    label={t("terminal.order.postalCode")}
                    value={form.site_postal_code}
                    onChange={update("site_postal_code")}
                    size="small"
                    required
                    sx={{ flex: "1 1 120px" }}
                    inputProps={{ maxLength: 10, "data-testid": "terminal-order-site-postal-code" }}
                  />
                  <TextField
                    label={t("terminal.order.city")}
                    value={form.site_city}
                    onChange={update("site_city")}
                    size="small"
                    required
                    sx={{ flex: "2 1 180px" }}
                    inputProps={{ "data-testid": "terminal-order-site-city" }}
                  />
                </Box>
              </Box>
            </Box>

            <TerminalPriceList />

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || loadingOrg || !canSend}
          startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
          data-testid="terminal-order-send"
        >
          {t("terminal.order.send")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TerminalOrderDialog;
